import * as React from 'react';

import {
  accentAt,
  AreaChart,
  BarChart,
  HorizontalBarChart,
  LineChart,
  PieChart,
  StackedBarChart,
  type ChartKind,
  type ChartSeries,
  type ChartValueFormat
} from '../charts';
import { useLocale } from '../providers/UIProvider';
import { cn } from '../utils';
import { captureChartPng } from '../utils/capture-chart-png';
import { getTestId } from '../utils/testId';
import { Button } from './Button';
import { Icon } from './icons/Icon';

/** One wire data point — `y` is a decimal string so amounts survive the wire losslessly. */
export interface ChartCardPoint {
  x: string;
  y: string;
}

/** One wire series — mirrors the juneau `AiChartPart` schema 1:1. */
export interface ChartCardSeries {
  label: string;
  /** ISO 4217 code when the series carries monetary values */
  currency?: string;
  points: ChartCardPoint[];
}

/** Target formats a chart export can produce. */
export type ChartCardExportFormat = 'pdf' | 'xlsx';

export interface ChartCardProps {
  kind: ChartKind;
  series: ChartCardSeries[];
  /** Optional heading above the chart */
  title?: string;
  yFormat?: ChartValueFormat;
  /** Locale-aware value formatter — defaults to `toLocaleString` */
  formatValue?: (value: number, currency?: string) => string;
  /** Chart height in px */
  height?: number;
  /** Export button handler — receives a lazy PNG capture of the rendered chart. */
  onExportClick?: (capturePng: () => Promise<string>) => void;
  /** Localized export button label; the button renders only when provided with `onExportClick`. */
  exportLabel?: string;
  className?: string;
  id?: string;
  testId?: string;
}

const formatInLocale = (value: number, locale: string, currency?: string): string =>
  currency
    ? value.toLocaleString(locale, { style: 'currency', currency, maximumFractionDigits: 2 })
    : value.toLocaleString(locale);

/** Converts the decimal-string wire series into the numeric chart core shape. */
export function toChartSeries(
  series: ChartCardSeries[],
  yFormat?: ChartValueFormat
): ChartSeries[] {
  return series.map(s => ({
    label: s.label,
    currency: yFormat === 'currency' ? s.currency : undefined,
    points: s.points.map(point => ({ x: point.x, y: Number(point.y) }))
  }));
}

/**
 * Card rendering one chart (bar, line or pie) from series data.
 * Data shape mirrors the juneau `AiChartPart` schema so AI chat parts map 1:1.
 */
export const ChartCard = React.forwardRef<HTMLDivElement, ChartCardProps>(
  (
    {
      kind,
      series,
      title,
      yFormat,
      formatValue,
      height = 240,
      onExportClick,
      exportLabel,
      className,
      id,
      testId
    },
    ref
  ) => {
    const locale = useLocale();
    const chartAreaRef = React.useRef<HTMLDivElement>(null);

    // Captures the rendered chart SVG lazily — only when an export is requested.
    const capturePng = React.useCallback((): Promise<string> => {
      const svg = chartAreaRef.current?.querySelector('svg');
      if (!svg) return Promise.reject(new Error('No chart SVG to capture.'));
      return captureChartPng(svg);
    }, []);
    const format = React.useCallback(
      (value: number, currency?: string): string =>
        formatValue ? formatValue(value, currency) : formatInLocale(value, locale, currency),
      [formatValue, locale]
    );

    const chartSeries = React.useMemo(() => toChartSeries(series, yFormat), [series, yFormat]);

    const formatTick = (value: number): string =>
      yFormat === 'currency'
        ? value.toLocaleString(locale, { notation: 'compact' })
        : value.toLocaleString(locale);

    const isEmpty = series.every(s => s.points.length === 0);
    const showLegend = series.length > 1;
    // One series has no legend to encode colour, so each bar gets its
    // own — with several series the colour must stay per-series.
    const getBarColor = showLegend
      ? undefined
      : (_: number, __: string | number, i: number) => accentAt(i);

    return (
      <div
        ref={ref}
        id={id}
        data-slot='chart-card'
        data-testid={getTestId({ testId, id })}
        className={cn(
          'bg-background-input text-foreground-base flex flex-col gap-sm rounded-xl border border-border-base p-md shadow-sm',
          className
        )}
      >
        {(title || (onExportClick && exportLabel && !isEmpty)) && (
          <div className='flex items-center justify-between gap-sm'>
            <h4 className='text-sm font-medium text-foreground-base leading-none'>{title}</h4>
            {onExportClick && exportLabel && !isEmpty && (
              <Button
                variant='ghost'
                size='sm'
                testId={testId ? `${testId}-export` : undefined}
                onClick={() => onExportClick(capturePng)}
              >
                <Icon name='download' size='sm' />
                {exportLabel}
              </Button>
            )}
          </div>
        )}
        {isEmpty ? (
          <span className='text-foreground-muted text-sm'>—</span>
        ) : (
          <div ref={chartAreaRef}>{renderChart()}</div>
        )}
      </div>
    );

    function renderChart(): React.ReactNode {
      return kind === 'pie' || kind === 'donut' ? (
        <PieChart
          series={chartSeries[0]}
          height={height}
          formatValue={format}
          // Same component — 'donut' just widens the hole beyond the pie default.
          innerRatio={kind === 'donut' ? 0.65 : undefined}
          ariaLabel={title}
        />
      ) : kind === 'horizontalBar' ? (
        <HorizontalBarChart
          series={chartSeries}
          height={height}
          formatValue={format}
          showLegend={showLegend}
          ariaLabel={title}
        />
      ) : kind === 'stackedBar' ? (
        <StackedBarChart
          series={chartSeries}
          height={height}
          formatValue={format}
          formatTick={formatTick}
          showLegend={showLegend}
          ariaLabel={title}
        />
      ) : kind === 'area' ? (
        <AreaChart
          series={chartSeries}
          height={height}
          formatValue={format}
          formatTick={formatTick}
          showLegend={showLegend}
          ariaLabel={title}
        />
      ) : kind === 'line' ? (
        <LineChart
          series={chartSeries}
          height={height}
          formatValue={format}
          formatTick={formatTick}
          showLegend={showLegend}
          ariaLabel={title}
        />
      ) : (
        <BarChart
          series={chartSeries}
          height={height}
          formatValue={format}
          formatTick={formatTick}
          showLegend={showLegend}
          getBarColor={getBarColor}
          ariaLabel={title}
        />
      );
    }
  }
);

ChartCard.displayName = 'ChartCard';
